import { useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  FileButton,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconDownload, IconFileText, IconTrash, IconUpload } from "@tabler/icons-react";
import { capitalizeLeadingCharacter } from "@shared/capitalizeLeadingCharacter";
import {
  getSelectedOtherOption,
  parseCheckboxAnswer,
  parseDocumentUploadAnswer,
  serializeDocumentUploadAnswer,
  type EcosystemHeadQuestion,
} from "../../../../../shared/ecosystemHeadQuestionnaire";
import { OPTION_LETTERS } from "@/pages/ecosystem-head/questionnaire-builder/questionnaireBuilder.const";
import { useUserContext } from "@/context/UserContext";
import trpc from "@/shared/utils/trpc";
import { errorNotification } from "@/shared/utils/errorNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

type Props = {
  question: EcosystemHeadQuestion;
  value: string;
  onChange: (value: string) => void;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  readOnly?: boolean;
  variant?: "default" | "typeform";
};

function TypeformOptionRow({
  letter,
  label,
  selected,
  multiple,
  readOnly,
  onClick,
}: {
  letter: string;
  label: string;
  selected: boolean;
  multiple: boolean;
  readOnly: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      type="button"
      className={`eh-qb-fill-option${selected ? " eh-qb-fill-option--selected" : ""}`}
      onClick={onClick}
      disabled={readOnly}
      aria-pressed={selected}
    >
      <Box className="eh-qb-fill-option-marker">
        {multiple ? (
          <Box
            className={`eh-qb-option-checkbox${selected ? " eh-qb-option-checkbox--selected" : ""}`}
            aria-hidden
          />
        ) : (
          <Box className={`eh-qb-option-radio${selected ? " eh-qb-option-radio--selected" : ""}`} aria-hidden />
        )}
        <Text size="sm" fw={700} c="#ffffff">
          {letter}
        </Text>
      </Box>
      <Text size="md" fw={selected ? 600 : 500} className="eh-qb-fill-option-label" c="#ffffff">
        {label}
      </Text>
    </UnstyledButton>
  );
}

function DocumentUploadField({
  value,
  onChange,
  readOnly,
  variant,
}: {
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  variant: "default" | "typeform";
}) {
  const { userDetails } = useUserContext();
  const companyId = userDetails?.company?.id;
  const resetRef = useRef<() => void>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const parsed = parseDocumentUploadAnswer(value);
  const isTypeform = variant === "typeform";
  const hasFile = Boolean(parsed.fileId);

  const updateAnswer = (patch: Partial<typeof parsed>) => {
    onChange(serializeDocumentUploadAnswer({ ...parsed, ...patch }));
  };

  const handleUpload = async (file: File | null) => {
    if (!file || readOnly) return;
    if (!companyId) {
      errorNotification({ title: "Upload unavailable", message: "Company profile is required to upload documents." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10 MB." });
      resetRef.current?.();
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "COMPANY_DOCUMENT");
      formData.append("companyId", companyId);
      formData.append("name", file.name);
      const uploaded = await trpc.file.upload.mutate(formData as unknown as FormData);
      updateAnswer({
        fileId: uploaded.id,
        fileName: file.name,
        explanation: undefined,
      });
    } catch (err) {
      console.error(err);
      errorNotification({ title: "Upload failed", message: "Could not upload the document. Try again." });
    } finally {
      resetRef.current?.();
      setUploading(false);
    }
  };

  const removeFile = () => {
    if (readOnly) return;
    updateAnswer({ fileId: undefined, fileName: undefined });
  };

  const handleDownload = async () => {
    if (!parsed.fileId) return;
    setDownloading(true);
    try {
      const result = await trpc.file.getFileUrl.query({ fileId: parsed.fileId, download: true });
      if (!result?.url) {
        errorNotification({
          title: "Download failed",
          message: "Could not download this document. Please try again.",
        });
        return;
      }
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDownloading(false);
    }
  };

  if (readOnly) {
    if (hasFile) {
      return (
        <Group justify="space-between" className={isTypeform ? "eh-qb-fill-doc-uploaded" : undefined} wrap="nowrap" gap="sm">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <IconFileText size={18} color={isTypeform ? "#ffffff" : undefined} />
            <Text size="sm" c={isTypeform ? "#ffffff" : undefined} lineClamp={2} style={{ wordBreak: "break-word" }}>
              {parsed.fileName ?? "Document uploaded"}
            </Text>
          </Group>
          <ActionIcon
            variant="light"
            color={isTypeform ? "gray" : "blue"}
            size="lg"
            radius="md"
            onClick={() => void handleDownload()}
            loading={downloading}
            aria-label={`Download ${parsed.fileName ?? "document"}`}
          >
            <IconDownload size={18} />
          </ActionIcon>
        </Group>
      );
    }
    return (
      <Text
        size="sm"
        c={isTypeform ? "#ffffff" : undefined}
        style={{ whiteSpace: "pre-wrap" }}
      >
        {parsed.explanation || "—"}
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {hasFile ? (
        <Group justify="space-between" className={isTypeform ? "eh-qb-fill-doc-uploaded" : undefined} wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <IconFileText size={18} color={isTypeform ? "#ffffff" : "#6B7280"} style={{ flexShrink: 0 }} />
            <Text size="sm" fw={500} lineClamp={2} c={isTypeform ? "#ffffff" : undefined} style={{ wordBreak: "break-word" }}>
              {parsed.fileName ?? "Document uploaded"}
            </Text>
          </Group>
          <ActionIcon variant="subtle" color="red" onClick={removeFile} aria-label="Remove document">
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ) : (
        <FileButton
          resetRef={resetRef}
          onChange={(file) => void handleUpload(file)}
          accept="application/pdf,image/png,image/jpeg,image/webp"
          disabled={uploading}
        >
          {(props) => (
            <Button
              {...props}
              variant={isTypeform ? "light" : "default"}
              color={isTypeform ? "gray" : undefined}
              leftSection={uploading ? <Loader size={16} /> : <IconUpload size={16} />}
              className={isTypeform ? "eh-qb-fill-doc-upload-btn" : undefined}
            >
              {uploading ? "Uploading..." : "Upload document"}
            </Button>
          )}
        </FileButton>
      )}

      {!hasFile ? (
        <Box>
          <Text size="sm" mb={6} c={isTypeform ? "#ffffff" : undefined} className={isTypeform ? "eh-qb-fill-doc-explanation-label" : undefined}>
            If you cannot upload the document, explain why
          </Text>
          <Textarea
            variant={isTypeform ? "unstyled" : "default"}
            value={parsed.explanation ?? ""}
            onChange={(event) => updateAnswer({ explanation: event.currentTarget.value })}
            minRows={2}
            autosize
            placeholder="Explain why the document is not available..."
            className={isTypeform ? "eh-qb-fill-text-input" : undefined}
            styles={
              isTypeform
                ? {
                    input: {
                      fontSize: "1rem",
                      lineHeight: 1.5,
                      color: "white",
                      borderBottom: "2px solid rgba(255,255,255,0.35)",
                      borderRadius: 0,
                      paddingInline: 0,
                    },
                  }
                : undefined
            }
          />
        </Box>
      ) : null}
    </Stack>
  );
}

export default function EcosystemHeadQuestionField({
  question,
  value,
  onChange,
  otherValue = "",
  onOtherChange,
  readOnly = false,
  variant = "default",
}: Props) {
  const isTypeform = variant === "typeform";
  const otherSelected = Boolean(getSelectedOtherOption(question, value));

  const otherSpecifyField =
    otherSelected && onOtherChange ? (
      isTypeform ? (
        <TextInput
          variant="unstyled"
          value={otherValue}
          onChange={(event) => onOtherChange(event.currentTarget.value)}
          readOnly={readOnly}
          placeholder="Other: Please specify..."
          className="eh-qb-fill-text-input"
          mt="sm"
          styles={{
            input: {
              fontSize: "var(--mantine-font-size-sm)",
              lineHeight: 1.5,
              color: "white",
              borderBottom: "2px solid rgba(255,255,255,0.4)",
              borderRadius: 0,
              paddingInline: 0,
            },
          }}
        />
      ) : (
        <TextInput
          mt="sm"
          label="Other: Please specify"
          value={otherValue}
          onChange={(event) => onOtherChange(event.currentTarget.value)}
          readOnly={readOnly}
          placeholder="Your answer"
        />
      )
    ) : null;

  if (question.type === "description") {
    return (
      <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
        {capitalizeLeadingCharacter(question.label)}
      </Text>
    );
  }

  if (question.type === "short_text") {
    if (isTypeform) {
      return (
        <TextInput
          variant="unstyled"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          readOnly={readOnly}
          placeholder="Type your answer here..."
          className="eh-qb-fill-text-input"
          styles={{
            input: {
              fontSize: "var(--mantine-font-size-sm)",
              lineHeight: 1.5,
              color: "white",
              borderBottom: "2px solid rgba(255,255,255,0.4)",
              borderRadius: 0,
              paddingInline: 0,
            },
          }}
        />
      );
    }

    return (
      <TextInput
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        readOnly={readOnly}
        placeholder="Your answer"
      />
    );
  }

  if (question.type === "multiple_choice") {
    if (isTypeform) {
      return (
        <Stack gap="xs">
          {(question.options ?? []).map((option, index) => (
            <TypeformOptionRow
              key={option.id}
              letter={OPTION_LETTERS[index] ?? String(index + 1)}
              label={option.label}
              selected={value === option.id}
              multiple={false}
              readOnly={readOnly}
              onClick={() => {
                if (readOnly) return;
                onChange(option.id);
              }}
            />
          ))}
          {otherSpecifyField}
        </Stack>
      );
    }

    return (
      <Stack gap="xs">
        <Radio.Group value={value} onChange={onChange}>
          {(question.options ?? []).map((option) => (
            <Radio key={option.id} value={option.id} label={option.label} disabled={readOnly} />
          ))}
        </Radio.Group>
        {otherSpecifyField}
      </Stack>
    );
  }

  if (question.type === "checkboxes") {
    const selected = parseCheckboxAnswer(value);

    if (isTypeform) {
      return (
        <Stack gap="xs">
          {(question.options ?? []).map((option, index) => {
            const isSelected = selected.includes(option.id);
            return (
              <TypeformOptionRow
                key={option.id}
                letter={OPTION_LETTERS[index] ?? String(index + 1)}
                label={option.label}
                selected={isSelected}
                multiple
                readOnly={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  const next = isSelected
                    ? selected.filter((id) => id !== option.id)
                    : [...selected, option.id];
                  onChange(JSON.stringify(next));
                }}
              />
            );
          })}
          {otherSpecifyField}
        </Stack>
      );
    }

    return (
      <Stack gap="xs">
        <Checkbox.Group value={selected} onChange={(next) => onChange(JSON.stringify(next))}>
          {(question.options ?? []).map((option) => (
            <Checkbox key={option.id} value={option.id} label={option.label} disabled={readOnly} />
          ))}
        </Checkbox.Group>
        {otherSpecifyField}
      </Stack>
    );
  }

  if (question.type === "document_upload") {
    return <DocumentUploadField value={value} onChange={onChange} readOnly={readOnly} variant={variant} />;
  }

  if (isTypeform) {
    return (
      <Textarea
        variant="unstyled"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        minRows={3}
        autosize
        readOnly={readOnly}
        placeholder="Type your answer here..."
        className="eh-qb-fill-text-input"
        styles={{
          input: {
            fontSize: "var(--mantine-font-size-sm)",
            lineHeight: 1.5,
            color: "white",
            borderBottom: "2px solid rgba(255,255,255,0.35)",
            borderRadius: 0,
            paddingInline: 0,
          },
        }}
      />
    );
  }

  return (
    <Textarea
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      minRows={3}
      autosize
      readOnly={readOnly}
      placeholder="Your answer"
    />
  );
}
